import React, { useEffect, useMemo, useState } from 'react';
import { Container, Box, Typography, Paper, Stack, CircularProgress } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from './store/store';
import ContactInfo from './features/onboarding/ContactInfo';
import CompanyDetails from './features/onboarding/CompanyDetails';
import BusinessDetails from './features/onboarding/BusinessDetails';
import OwnerDetails from './features/onboarding/OwnerDetails';
import Review from './features/onboarding/Review';
import Shareholders from './features/onboarding/Shareholders';
import RegisterAuthorize from './features/onboarding/RegisterAuthorize';
import OverviewHub from './features/onboarding/OverviewHub';
import PartnerOverview from './features/onboarding/PartnerOverview';
import MerchantConditions from './features/onboarding/MerchantConditions';
import PartnerConditions from './features/onboarding/PartnerConditions';
import PartnerDetails from './features/onboarding/PartnerDetails';
import RapydAgreement from './features/onboarding/RapydAgreement';
import PartnerAgreement from './features/onboarding/PartnerAgreement';
import DirectDebitSetup from './features/onboarding/DirectDebitSetup';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import themeBase from './theme';
import { fetchBrand, BrandConfig, isPartnerAudience } from './api/brand';
import { setBrandConfig, setChecklist, setPartnerProfile, setView } from './features/onboarding/onboardingSlice';
import { getOnboardingApplicationId, hasOnboardingToken } from './api/client';
import { fetchChecklist, fetchMyApplication } from './features/onboarding/checklistApi';
import { fetchMyPartner } from './features/onboarding/partnerApi';

function App() {
  const dispatch = useDispatch();
  const { activeStep, formData, view } = useSelector((state: RootState) => state.onboarding);

  const [brand, setBrand] = useState<BrandConfig | null>(null);
  const [brandLoading, setBrandLoading] = useState<boolean>(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromDd = params.get('dd') === 'success' || params.get('dd') === 'cancel';
    if (fromDd) dispatch(setView('hub'));
    if (!hasOnboardingToken()) return;
    (async () => {
      try {
        if (isPartnerAudience()) {
          dispatch(setPartnerProfile(await fetchMyPartner()));
          dispatch(setView('hub'));
          return;
        }
        const storedId = getOnboardingApplicationId();
        const existing = storedId
          ? await fetchChecklist(storedId)
          : await fetchMyApplication();
        dispatch(setChecklist(existing));
        dispatch(setView('hub'));
      } catch (_) { /* stay on login if token invalid */ }
    })();
  }, [dispatch]);

  useEffect(() => {
    (async () => {
      try {
        const host = window.location.hostname;
        const cfg = await fetchBrand(host);
        setBrand(cfg);
        dispatch(setBrandConfig(cfg));
        try { (window as any).__BRAND__ = cfg; } catch (_) {}
        // set favicon if provided
        if (cfg.favicon_url) {
          let link: HTMLLinkElement | null = document.querySelector("link[rel='icon']");
          if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
          link.href = cfg.favicon_url;
        }
        // set document title
        const baseTitle = (isPartnerAudience() || cfg.audience === 'partner')
          ? 'Start your partner onboarding'
          : 'Start your merchant onboarding';
        document.title = cfg.brand_name ? `${baseTitle} – ${cfg.brand_name}` : baseTitle;
      } catch (_) {
        setBrand(null);
      } finally {
        setBrandLoading(false);
      }
    })();
  }, [dispatch]);

  const runtimeTheme = useMemo(() => {
    if (!brand) return themeBase;
    return createTheme({
      ...themeBase,
      palette: {
        ...themeBase.palette,
        primary: {
          ...(themeBase.palette.primary as any),
          main: brand.primary_color || (themeBase.palette.primary as any).main,
          contrastText: brand.primary_text_color || (themeBase.palette.primary as any).contrastText,
        },
        background: {
          ...(themeBase.palette.background as any),
          default: brand.background_color || (themeBase.palette.background as any).default,
        },
      },
    });
  }, [brand]);

  const wizardLabels = formData.companyType === 'COMPANY'
    ? ['Company Details','Business Details','Owners','Confirm & continue','Review & Submit']
    : ['Company Details','Business Details','Owner Details','Confirm & continue','Review & Submit'];

  const wizardComponents = formData.companyType === 'COMPANY'
    ? [<CompanyDetails/>, <BusinessDetails/>, <Shareholders/>, <RegisterAuthorize/>, <Review/>]
    : [<CompanyDetails/>, <BusinessDetails/>, <OwnerDetails/>, <RegisterAuthorize/>, <Review/>];

  const wizardIndex = Math.max(0, activeStep - 1);
  const isPartner = isPartnerAudience() || brand?.audience === 'partner';
  const heading = view === 'login' ? 'Sign in'
    : view === 'hub' ? 'Your onboarding'
    : view === 'conditions' ? (isPartner ? 'Partner conditions' : 'Merchant conditions')
    : view === 'partnerDetails' ? 'Partner details'
    : view === 'agreement' ? (isPartner ? 'Partner agreement' : 'Rapyd agreement')
    : view === 'directDebit' ? 'Direct Debit'
    : (wizardLabels[wizardIndex] || 'Secure onboarding');

  const logoSrc = brand?.logo_url || '/logo-dark-s.png';
  const contactPhone = brand?.contact_phone || '+441174636345';
  const contactEmail = brand?.contact_email || 'info@synthiax.io';

  if (brandLoading) {
    // Block rendering until brand is fetched; show full-screen loader
    return (
      <ThemeProvider theme={themeBase}>
        <Box sx={{ minHeight: '100vh', bgcolor: '#003047', color: brand?.background_text_color || 'common.white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress color="inherit" thickness={4} />
            <Typography variant="body2" sx={{ opacity: 0.9 }}>Loading…</Typography>
          </Stack>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={runtimeTheme}>
      <Box sx={{ minHeight: '100vh', bgcolor: brand?.background_color || '#003047', color: brand?.background_text_color }}>
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 6 } }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '420px 1fr' }, gap: { xs: 3, md: 5 } }}>
            {/* Left hero panel */}
            <Paper elevation={0} sx={{ bgcolor: 'transparent', color: brand?.background_text_color || 'common.white', p: { xs: 2, md: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box component="img" src={logoSrc} alt="Brand Logo" sx={{ height: { xs: 40, md: 56 } }} />
              </Box>
              <Typography variant="h2" sx={{ fontWeight: 800, lineHeight: 1.05, mb: 2 }}>
                {isPartner ? 'Start your partner onboarding' : 'Start your merchant onboarding'}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.85, maxWidth: 560 }}>
                {isPartner
                  ? 'Review the partner conditions, tell us about your business, and sign the partner agreement. We use this to set you up as a SynthiaX distributor.'
                  : 'Tell us about your business, review the merchant conditions, and sign the agreement so we can set up card payments for you.'}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.85, maxWidth: 560, mt: 1 }}>
                For any questions about pricing, getting started, or for technical support, please contact:
              </Typography>
              <Box sx={{ mt: 4 }}>
                <Stack spacing={0.5}>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>WhatsApp: {contactPhone}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Email: {contactEmail}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Mon–Fri 09:00–18:00</Typography>
                </Stack>
              </Box>
            </Paper>

            {/* Right form card */}
            <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3, bgcolor: 'background.paper' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{heading}</Typography>
                {view === 'wizard' && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    {wizardLabels.map((_, idx) => (
                      <Box key={idx} sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: idx <= wizardIndex ? 'primary.main' : 'grey.400', opacity: idx === wizardIndex ? 1 : 0.7 }} />
                    ))}
                  </Stack>
                )}
              </Stack>

              <Box>
                {view === 'login' && <ContactInfo />}
                {view === 'hub' && (isPartner ? <PartnerOverview /> : <OverviewHub />)}
                {view === 'conditions' && (isPartner ? <PartnerConditions /> : <MerchantConditions />)}
                {view === 'partnerDetails' && isPartner && <PartnerDetails />}
                {view === 'agreement' && (isPartner ? <PartnerAgreement /> : <RapydAgreement />)}
                {view === 'directDebit' && !isPartner && <DirectDebitSetup />}
                {view === 'wizard' && !isPartner && (wizardComponents[wizardIndex] || <div />)}
              </Box>
            </Paper>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default App;